import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Newspaper, ExternalLink, Clock, RefreshCw, Settings, 
  CheckCircle, XCircle, Rss, Globe, Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const OSINT_BACKEND = process.env.REACT_APP_BACKEND_URL;

// Feed icon mapping
const feedIcons = {
  cyberscoop: '🛡️',
  darkreading: '📖',
  hackernoon: '💻',
  helpnetsecurity: '🔐',
  krebsonsecurity: '🔍',
  securitymagazine: '📰',
  securityweek: '🗞️',
  techcrunch: '🚀',
  thedfirreport: '🔬',
  thehackernews: '📡',
  threatpost: '⚠️',
  therecord: '📝',
  theregister: '📋',
  theverge: '🌐',
  wired: '⚡',
  zdnet: '💡',
  default: '📰'
};

const Newsfeed = () => {
  const [news, setNews] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('news');
  const [selectedSource, setSelectedSource] = useState('all');
  const [updatingFeed, setUpdatingFeed] = useState(null);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${OSINT_BACKEND}/api/osint/newsfeed`);
      setNews(response.data || []);
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error('Failed to load news feed');
    }
  };

  const fetchFeeds = async () => {
    try {
      const response = await axios.get(`${OSINT_BACKEND}/api/osint/settings/modules/newsfeed/`);
      setFeeds(response.data || []);
    } catch (error) {
      console.error('Error fetching feed settings:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchNews(), fetchFeeds()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchNews();
    setRefreshing(false);
    toast.success('News feed refreshed');
  };

  const toggleFeed = async (feedName, currentState) => {
    setUpdatingFeed(feedName);
    try {
      const endpoint = currentState 
        ? `${OSINT_BACKEND}/api/osint/settings/modules/newsfeed/disable`
        : `${OSINT_BACKEND}/api/osint/settings/modules/newsfeed/enable`;
      
      await axios.post(`${endpoint}?feedName=${encodeURIComponent(feedName)}`, {});
      
      // Update local state
      setFeeds(prev => prev.map(feed => 
        feed.name === feedName ? { ...feed, enabled: !currentState } : feed
      ));
      
      toast.success(`${feedName} ${currentState ? 'disabled' : 'enabled'}`);
      
      // Refresh news after toggling
      await fetchNews();
    } catch (error) {
      console.error('Error toggling feed:', error);
      toast.error('Failed to update feed settings');
    } finally {
      setUpdatingFeed(null);
    }
  };

  const getFeedIcon = (iconName) => {
    return feedIcons[iconName?.toLowerCase()] || feedIcons.default;
  };

  const filteredNews = selectedSource === 'all' 
    ? news 
    : news.filter(article => article.feedname === selectedSource);

  const uniqueSources = [...new Set(news.map(article => article.feedname))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading cybersecurity news...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold font-heading mb-2" data-testid="newsfeed-title">
            Cybersecurity Newsfeed
          </h2>
          <p className="text-muted-foreground">
            Latest news from {feeds.filter(f => f.enabled).length} cybersecurity sources
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={handleRefresh} 
            disabled={refreshing}
            data-testid="refresh-news-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="news" data-testid="news-tab">
            <Newspaper className="w-4 h-4 mr-2" />
            News Feed ({filteredNews.length})
          </TabsTrigger>
          <TabsTrigger value="sources" data-testid="sources-tab">
            <Rss className="w-4 h-4 mr-2" />
            Manage Sources ({feeds.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="news">
          {/* Source Filter */}
          <Card className="mb-6">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground mr-2">Filter by source:</span>
                <Button
                  variant={selectedSource === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedSource('all')}
                  className="text-xs"
                >
                  All Sources
                </Button>
                {uniqueSources.slice(0, 8).map(source => (
                  <Button
                    key={source}
                    variant={selectedSource === source ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedSource(source)}
                    className="text-xs"
                  >
                    {getFeedIcon(source)} {source}
                  </Button>
                ))}
                {uniqueSources.length > 8 && (
                  <span className="text-xs text-muted-foreground">+{uniqueSources.length - 8} more</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* News Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="news-grid">
            {filteredNews.length === 0 ? (
              <div className="col-span-full">
                <Card>
                  <CardContent className="py-12 text-center">
                    <Newspaper className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-2">No news articles found.</p>
                    <p className="text-sm text-muted-foreground">
                      Enable more sources in the "Manage Sources" tab.
                    </p>
                  </CardContent>
                </Card>
              </div>
            ) : (
              filteredNews.map((article, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow group" data-testid={`news-card-${index}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start gap-2">
                      <span className="text-2xl flex-shrink-0">{getFeedIcon(article.icon)}</span>
                      <CardTitle className="text-base line-clamp-2 group-hover:text-primary transition-colors">
                        {article.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                      {article.summary}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>{article.date}</span>
                      </div>
                      <a
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                        data-testid={`read-more-${index}`}
                      >
                        Read More
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="mt-3 pt-3 border-t flex items-center justify-between">
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                        {article.feedname}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="sources">
          <div className="space-y-4">
            {/* Info Card */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="py-4">
                <div className="flex gap-3">
                  <Globe className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">Open Source RSS Feeds</p>
                    <p>
                      These are free, open-source RSS feeds from major cybersecurity news outlets. 
                      Toggle sources on/off to customize your news feed. Changes take effect immediately.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Feeds Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="feeds-grid">
              {feeds.map((feed, index) => (
                <Card 
                  key={index} 
                  className={`transition-all ${feed.enabled ? 'border-green-200 bg-green-50/30' : 'border-gray-200 opacity-75'}`}
                  data-testid={`feed-card-${feed.name}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-2xl flex-shrink-0">{getFeedIcon(feed.icon)}</span>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold truncate">{feed.name}</h4>
                          <p className="text-xs text-muted-foreground truncate">{feed.url}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        {feed.enabled ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <XCircle className="w-4 h-4 text-gray-400" />
                        )}
                        <Switch
                          checked={feed.enabled}
                          onCheckedChange={() => toggleFeed(feed.name, feed.enabled)}
                          disabled={updatingFeed === feed.name}
                          data-testid={`toggle-${feed.name}`}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Summary */}
            <Card>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="font-medium">{feeds.filter(f => f.enabled).length}</span>
                    <span className="text-muted-foreground"> of </span>
                    <span className="font-medium">{feeds.length}</span>
                    <span className="text-muted-foreground"> sources enabled</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={async () => {
                    setLoading(true);
                    await Promise.all([fetchNews(), fetchFeeds()]);
                    setLoading(false);
                  }}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh List
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Newsfeed;
